/**
 * MiActividad — Pantalla donde cada usuario ve sus propias acciones auditadas
 * en los últimos N días.
 *
 * Origen: incidente del 28-jul-2026. Idea: si cada usuario ve su propia
 * huella, se refuerza la conciencia de que "todo queda registrado" mucho
 * más que un mensaje abstracto. Es prevención por transparencia.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Select, MenuItem, FormControl, InputLabel, TextField, Alert,
  Skeleton, IconButton, Tooltip, Button,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

// Firebase entrega el ID token en el momento — patrón real de auth del sistema.
async function getFirebaseAuthHeaders() {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}
function getUserRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}

// Colores por familia de acción (ayuda a escanear la tabla visualmente).
function colorAccion(accion) {
  const a = (accion || '').toLowerCase();
  if (a.includes('elimin') || a.includes('desactiv') || a.includes('cancel')) return '#dc2626';
  if (a.includes('cre') || a.includes('nuev')) return '#059669';
  if (a.includes('edit') || a.includes('actualiz') || a.includes('cambi')) return '#0d9488';
  if (a.includes('aprob') || a.includes('confirm')) return '#0891b2';
  if (a.includes('pago') || a.includes('cobr') || a.includes('voucher')) return '#7c3aed';
  return '#64748b';
}

function fmtFechaLocal(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-CO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
  } catch { return iso; }
}

export default function MiActividad() {
  const navigate = useNavigate();
  const role = getUserRole();
  const puedeVerTodos = role === 'superadmin' || role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dias, setDias] = useState(30);
  const [search, setSearch] = useState('');

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      // Firebase puede tardar en tener el user listo tras el mount del componente
      let user = getAuth().currentUser;
      let intentos = 0;
      while (!user && intentos < 5) {
        await new Promise(r => setTimeout(r, 400));
        user = getAuth().currentUser;
        intentos++;
      }
      if (!user) throw new Error('Sesión no lista aún. Recarga la página.');
      const headers = await getFirebaseAuthHeaders();
      const r = await fetch(`${API_BASE}/turismo/auditoria/mi-actividad?dias=${dias}&limit=500`,
        { headers });
      if (r.status === 401) throw new Error('Tu sesión expiró. Cierra sesión y vuelve a entrar.');
      if (!r.ok) throw new Error('No se pudo cargar tu actividad');
      const data = await r.json();
      setRows(data.acciones || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [dias]);

  const filtrados = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(r =>
      (r.accion || '').toLowerCase().includes(s) ||
      (r.entidad_codigo || '').toLowerCase().includes(s) ||
      (r.descripcion || '').toLowerCase().includes(s)
    );
  }, [rows, search]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2,
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(30, 58, 95, 0.35)',
        }}>
          <ShieldIcon sx={{ color: '#fbbf24', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
            Mi actividad
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
            Todas las operaciones que has realizado en el sistema
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
        Esto es el mismo registro que ven los administradores. Cada acción se guarda
        con tu email, tu IP y la hora exacta. Es tu evidencia de las buenas
        prácticas que sigues día a día.
      </Alert>

      {puedeVerTodos && (
        <Alert severity="success" sx={{ mb: 2, fontSize: 13,
          '& .MuiAlert-message': { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' } }}>
          <Box>
            <strong>Tienes rol {role}</strong> — puedes revisar la actividad
            de cualquier usuario del sistema, con filtro por usuario/acción/fecha.
          </Box>
          <Button variant="contained" size="small" endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/trazabilidad')}
            sx={{ textTransform: 'none', fontWeight: 700,
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)' }}>
            Ir a Trazabilidad
          </Button>
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Ventana</InputLabel>
          <Select value={dias} label="Ventana" onChange={e => setDias(e.target.value)}>
            <MenuItem value={7}>Últimos 7 días</MenuItem>
            <MenuItem value={30}>Últimos 30 días</MenuItem>
            <MenuItem value={90}>Últimos 90 días</MenuItem>
            <MenuItem value={180}>Últimos 180 días</MenuItem>
            <MenuItem value={365}>Últimos 365 días</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" placeholder="Buscar por acción, código o descripción…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#94a3b8', mr: 1 }} /> }}
          sx={{ flex: 1, minWidth: 240 }} />
        <Tooltip title="Recargar">
          <IconButton onClick={cargar} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            {loading ? 'Cargando…' :
             filtrados.length === 0 ? 'No hay actividad en este rango' :
             `${filtrados.length} acción(es)`}
          </Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 11.5, color: '#475569' }}>Fecha y hora</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11.5, color: '#475569' }}>Acción</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11.5, color: '#475569' }}>Entidad</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11.5, color: '#475569' }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11.5, color: '#475569' }}>Origen</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11.5, color: '#475569' }}>IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton width="80%" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filtrados.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {fmtFechaLocal(r.created_at_utc)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.accion}
                      sx={{ bgcolor: `${colorAccion(r.accion)}15`, color: colorAccion(r.accion),
                             fontWeight: 700, fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {r.entidad_codigo || (r.entidad_tipo ? `${r.entidad_tipo}#${r.entidad_id}` : '—')}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#334155', maxWidth: 400 }}>
                    {r.descripcion || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.origen || '—'}
                      variant="outlined" sx={{ fontSize: 10.5, height: 20 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', color: '#64748b' }}>
                    {r.ip || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
